import { Box, HStack, Text, useToken } from "@chakra-ui/react";
import { progressPercent } from "../reviewLogic";

/**
 * InlineProgress — a tiny 16px progress ring + "N/M accounts reviewed" readout
 * for the resource drawer rows (PLO-4803). Azurite fill, green when complete.
 * Ports design.tsx `InlineProgress` (~lines 1768–1786).
 */
export interface InlineProgressProps {
  value: number;
  total: number;
  complete?: boolean;
  /** trailing readout copy suffix; defaults to "accounts reviewed" */
  noun?: string;
}

const InlineProgress = ({
  value,
  total,
  complete = false,
  noun = "accounts reviewed",
}: InlineProgressProps) => {
  const dim = 16;
  const stroke = 2.5;
  const r = (dim - stroke) / 2;
  const c = dim / 2;
  const circ = 2 * Math.PI * r;
  const pct = progressPercent(value, total) / 100;

  const [track, azurite, success] = useToken("colors", [
    "neutral.100",
    "azurite.600",
    "success.600",
  ]);
  const fg = complete ? success : azurite;

  return (
    <HStack as="span" spacing="2xs" flexShrink={0}>
      <Box as="span" w="16px" h="16px" flexShrink={0} lineHeight="0">
        <svg
          width={dim}
          height={dim}
          viewBox={`0 0 ${dim} ${dim}`}
          style={{ transform: "rotate(-90deg)" }}
          aria-hidden
        >
          <circle cx={c} cy={c} r={r} fill="none" stroke={track} strokeWidth={stroke} />
          <circle
            cx={c}
            cy={c}
            r={r}
            fill="none"
            stroke={fg}
            strokeWidth={stroke}
            strokeLinecap="butt"
            strokeDasharray={circ}
            strokeDashoffset={circ * (1 - pct)}
          />
        </svg>
      </Box>
      <Text
        fontSize="sm"
        fontWeight={500}
        color="text.body.secondary"
        whiteSpace="nowrap"
      >
        {value}/{total} {noun}
      </Text>
    </HStack>
  );
};

export default InlineProgress;
